import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  InfoOutlined,
  CheckCircleOutline,
  WarningAmber,
  ErrorOutline
} from '@mui/icons-material';

const LogMessage = React.memo(({ message, type, timestamp }) => {
  // Auto-detect type from message content if not provided
  const detectedType = type || detectMessageType(message);
  
  const getIconAndColor = () => {
    switch (detectedType) {
      case 'success':
        return { icon: CheckCircleOutline, color: 'success.main' };
      case 'warning':
        return { icon: WarningAmber, color: 'warning.main' };
      case 'error':
        return { icon: ErrorOutline, color: 'error.main' };
      case 'info':
      default:
        return { icon: InfoOutlined, color: 'info.main' };
    }
  };

  const { icon: Icon, color } = getIconAndColor();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        mb: 0.5,
        p: 0.5,
        '&:hover': {
          bgcolor: 'action.hover',
          borderRadius: 1
        }
      }}
    >
      <Icon
        sx={{
          fontSize: 16,
          color,
          mr: 1,
          mt: 0.25,
          flexShrink: 0
        }}
      />
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontFamily: '"Roboto Mono", monospace',
          fontSize: 13,
          lineHeight: 1.6,
          color: 'text.primary',
          flex: 1,
          wordBreak: 'break-word'
        }}
      >
        {message}
      </Typography>
      {timestamp && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontFamily: '"Roboto Mono", monospace',
            fontSize: 11,
            ml: 1,
            flexShrink: 0
          }}
        >
          {formatTimestamp(timestamp)}
        </Typography>
      )}
    </Box>
  );
});

// Detect message type from content
function detectMessageType(message) {
  const msgLower = message.toLowerCase();
  
  if (message.includes('❌') || msgLower.includes('error') || msgLower.includes('failed')) {
    return 'error';
  }
  if (message.includes('⚠️') || msgLower.includes('warning') || msgLower.includes('not found')) {
    return 'warning';
  }
  if (message.includes('✓') || msgLower.includes('complete') || msgLower.includes('calculated')) {
    return 'success';
  }
  return 'info';
}

// Format timestamp for display
function formatTimestamp(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { 
    hour12: false, 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  });
}

LogMessage.displayName = 'LogMessage';

export default LogMessage;

